import { faFilter } from "@fortawesome/pro-regular-svg-icons";
import { faFilter as faFilterSolid } from "@fortawesome/pro-solid-svg-icons";
import { CloseButton } from "@headlessui/react";
import { endOfDay, startOfDay } from "date-fns";
import { SelectRangeEventHandler } from "react-day-picker";

import { DateRangePicker } from "src/base-components/DateRangePicker";
import {
  URLParamFilter,
  useFilter,
} from "src/base-components/tableColumnFilters/useFilters";
import { Button } from "src/design-system/Button";
import { IconButton } from "src/design-system/Icon";

const getDateFromFilters = (filter: URLParamFilter | undefined) => {
  if (!filter) return undefined;

  const date = new Date(filter.values[0]);
  return isNaN(date.getTime()) ? undefined : date;
};

export const DateRangeFilter: React.FC<{
  property: string;
  dataLoc: string;
}> = ({ property, dataLoc }) => {
  const { filters, update, apply, reset, isFiltering, isApplied } = useFilter(
    property,
    [
      { key: property, operator: "gte", values: [] },
      { key: property, operator: "lte", values: [] },
    ],
  );

  const handleChange: SelectRangeEventHandler = (value) => {
    if (value?.from || value?.to) {
      const from = startOfDay((value.from ?? value.to) as Date);
      const to = endOfDay((value.to ?? value.from) as Date);

      update([
        {
          key: property,
          operator: "gte",
          values: [from.toISOString()],
        },
        {
          key: property,
          operator: "lte",
          values: [to.toISOString()],
        },
      ]);
    }
  };

  const isFilteringProperty = isFiltering({ key: property });

  return (
    <span>
      <DateRangePicker
        buttonRenderer={({ open }) => (
          <IconButton
            isActive={open || isFilteringProperty}
            dataLoc={dataLoc}
            icon={open || isFilteringProperty ? faFilterSolid : faFilter}
            size="2xs"
          />
        )}
        footerRenderer={() => (
          <div className="mt-2 flex justify-between border-gray-200 border-t p-4 pb-2">
            <CloseButton
              as={Button}
              dataLoc={`${dataLoc}-reset-button`}
              isDisabled={!isFilteringProperty}
              size="sm"
              variant="secondary"
              onClick={() => reset()}
            >
              Reset
            </CloseButton>
            <CloseButton
              as={Button}
              dataLoc={`${dataLoc}-apply-button`}
              isDisabled={isApplied()}
              size="sm"
              variant="primary"
              onClick={apply}
            >
              Apply
            </CloseButton>
          </div>
        )}
        value={{
          from: getDateFromFilters(filters.at(0)),
          to: getDateFromFilters(filters.at(1)),
        }}
        resetable
        usePredefinedDates
        onChange={handleChange}
        onReset={reset}
      />
    </span>
  );
};
